import { useState, useEffect, useRef, useCallback } from 'react'
import { apiClient } from '@/services/api'
import type { ApprovalFlowNode } from '@/types'

interface UseApprovalPreviewParams {
  applicationId: string | number | null
  /** 申请人 ID，变化时重新拉取审批流 */
  applicantId: string
  /** 申请人姓名，随 applicantId 一起传给后端 */
  applicantName?: string
  /** 同行人列表，新增/删除/修改时重新拉取审批流 */
  companions: Array<{ employeeId: string; name: string }>
  /** 当前表单的开始时间（ISO 格式），覆盖 DB 旧数据 */
  startDate?: string
  /** 当前表单的结束时间（ISO 格式），覆盖 DB 旧数据 */
  endDate?: string
  /**
   * 出差类别。
   * - 同城（LOCAL）：日期变化影响 FORM_CC02_LENGTH 路由条件
   * - 跨城（CROSS_CITY）：日期变化影响 FORM_BUSINESS_TRIP_BUSINESS_TRIP_HOUR 流程变量（出差时长）
   * 两种类别均需监听日期变化以保持审批预览数据准确
   */
  category?: string | null
}

interface UseApprovalPreviewReturn {
  nodes: ApprovalFlowNode[]
  rid: string | null
  isLoading: boolean
  error: string | null
  refresh: () => void
}

const DEBOUNCE_DELAY = 1500

/**
 * 审批流程预览 Hook（FR-024）
 *
 * 触发条件：
 * - 人员变化：申请人变更、同行人新增/删除/修改
 * - 日期变化：开始/结束时间变更（影响同城的路由条件和跨城的出差时长变量）
 * - 1500ms 防抖避免频繁请求
 */
const useApprovalPreview = ({
  applicationId,
  applicantId,
  applicantName,
  companions,
  startDate,
  endDate
}: UseApprovalPreviewParams): UseApprovalPreviewReturn => {
  const [nodes, setNodes] = useState<ApprovalFlowNode[]>([])
  const [rid, setRid] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 用 ref 存最新的 companions，避免 fetchPreview 依赖它
  const companionsRef = useRef(companions)
  useEffect(() => {
    companionsRef.current = companions
  })

  // 用 ref 存最新的 applicantId/applicantName/dates，避免 fetchPreview 依赖它
  const applicantIdRef = useRef(applicantId)
  const applicantNameRef = useRef(applicantName)
  const startDateRef = useRef(startDate)
  const endDateRef = useRef(endDate)
  useEffect(() => {
    applicantIdRef.current = applicantId
    applicantNameRef.current = applicantName
    startDateRef.current = startDate
    endDateRef.current = endDate
  })

  // 将同行人列表转为稳定的字符串 key，只在人员 ID 真正变化时才变
  // 排序保证顺序无关（添加顺序不影响审批流）
  const companionsKey = companions.map(c => c.employeeId).sort().join(',')

  const fetchPreview = useCallback(async () => {
    if (!applicationId) return
    setIsLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.getApprovalPreview(
        String(applicationId),
        companionsRef.current,
        applicantIdRef.current,
        applicantNameRef.current,
        startDateRef.current,
        endDateRef.current,
      )
      setNodes(data?.nodes ?? [])
      setRid(data?.rid ?? null)
    } catch (err) {
      console.error('审批预览请求失败:', err)
      setError('未能预测完整审批流，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }, [applicationId])

  const refresh = useCallback(() => {
    if (applicationId && applicantId) fetchPreview()
  }, [applicationId, applicantId, fetchPreview])

  useEffect(() => {
    // 必须有草稿 ID 和申请人才能拉取审批流
    if (!applicationId || !applicantId) return
    const timer = setTimeout(fetchPreview, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  // companionsKey 是稳定字符串，只在人员真正变化时触发。
  // 同城（LOCAL）与跨城（CROSS_CITY）均需在日期变化时重新拉取：同城影响 FORM_CC02_LENGTH，跨城影响 FORM_BUSINESS_TRIP_BUSINESS_TRIP_HOUR。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicationId,
    applicantId,
    companionsKey,
    fetchPreview,
    startDate,
    endDate,
  ])

  return { nodes, rid, isLoading, error, refresh }
}

export default useApprovalPreview
